import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Drawer, DrawerContent } from '../../components/ui/drawer';
import { Sheet, SheetContent } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Send, ArrowRight, FileText, Search, Download, SlidersHorizontal, ChevronRight } from 'lucide-react';
import { TablePagination } from '../../components/ui/table-pagination';
import LeadDetailModal from '../../components/LeadDetailModal';
import {
  mapActiveLoanTypes,
  getRootLoanTypes,
  getChildLoanTypes,
  resolveLeafLoanType,
} from '../../lib/loanTypeHierarchy';

const EMPTY_LEAD_FORM = {
  customer_name: '',
  customer_mobile: '',
  parent_loan_type_id: '',
  sub_loan_type_id: '',
  loan_amount: '',
  location: '',
  notes: '',
};

const statusConfig = {
  application_submitted: { label: 'Application Submitted', cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
  bank_assigned: { label: 'Bank Assigned', cls: 'bg-sky-100 text-sky-700 border border-sky-200' },
  sanctioned: { label: 'Sanctioned - Loan Approved', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  sanctioned_pdd: { label: 'Sanctioned - PDD', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-300' },
  declined: { label: 'Declined - Loan Rejected', cls: 'bg-red-100 text-red-700 border border-red-200' },
  on_hold: { label: 'On Hold - Under Evaluation', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  action_required: { label: 'Action Required - Upload Documents', cls: 'bg-orange-100 text-orange-800 border border-orange-200' },
  disbursed: { label: 'Disbursed', cls: 'bg-teal-100 text-teal-700 border border-teal-200' },
  commission_rejected: { label: 'Commission Rejected', cls: 'bg-red-100 text-red-700 border border-red-200' },
  commission_processing: { label: 'Commission Processing', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  commission_paid: { label: 'Commission Paid', cls: 'bg-green-100 text-green-800 border border-green-200' },
};

// Exported as standalone so it can be used without a component context
function buildLeadsCsv(allLeads) {
  const headers = ['Application ID', 'Applicant Name', 'Loan Type', 'Loan Amount', 'Current Stage', 'Expected Commission', 'Final Commission', 'Commission Paid'];
  const rows = allLeads.map(l => [
    l.crm_lead_id || l.lead_reference || '-',
    l.customer_name || '-',
    l.product_name || '-',
    l.loan_amount || '-',
    statusConfig[l.current_status]?.label || l.current_status || '-',
    l.commission_amount || 0,
    l.bank_name && l.commission_amount ? l.commission_amount : 0,
    l.commission_paid || 0,
  ]);
  return [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
}

function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function fetchAndExportLeads({ dateParams, loanTypeFilter, stageFilter, searchQuery }) {
  const res = await api.get(`/api/leads/my?page=1&page_size=10000${dateParams}`);
  let allLeads = res.data.leads || [];
  if (loanTypeFilter) allLeads = allLeads.filter(l => (l.product_name || '') === loanTypeFilter);
  if (stageFilter) allLeads = allLeads.filter(l => l.current_status === stageFilter);
  if (searchQuery) allLeads = allLeads.filter(l =>
    (l.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (l.crm_lead_id || l.lead_reference || '').toLowerCase().includes(searchQuery.toLowerCase())
  );
  return allLeads;
}

async function processCommissionPreviews(rangeableLeads) {
  const previewPromises = rangeableLeads
    .filter(l => l.id)
    .map(l =>
      api.get(`/api/admin/leads/${l.id}/commission-preview`)
        .then(r => ({ id: l.id, data: r.data }))
        .catch(() => null)
    );
  return Promise.all(previewPromises);
}

export const fmtShortCurrency = (amt) => {
  if (!amt) return '₹ 0';
  const num = Number(amt);
  if (num < 1000) return `₹ ${num}`;
  if (num < 100000) {
    const val = num / 1000;
    return `₹ ${parseFloat(val.toFixed(1))}K`;
  }
  if (num < 10000000) {
    const val = num / 100000;
    return `₹ ${parseFloat(val.toFixed(2))} Lac`;
  }
  const val = num / 10000000;
  return `₹ ${parseFloat(val.toFixed(2))} Cr`;
};

const fmtCurrency = (amt) => amt ? `₹ ${Number(amt).toLocaleString('en-IN')}` : '₹ 0';

// Loading skeleton component extracted to reduce cognitive complexity
function PartnerDashboardSkeleton() {
  return (
    <div className="space-y-4" data-testid="partner-dashboard-skeleton">
      {/* KPI Cards Skeleton */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        {[1,2,3,4,5].map(i => (
          <div key={`kpi-skel-${i}`} className={`${i === 4 ? 'col-span-2 lg:col-span-1' : ''} bg-white rounded-xl p-2.5 sm:p-4 border border-gray-100 shadow-sm animate-pulse`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="h-3 w-20 bg-gray-200 rounded" />
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-gray-200" />
            </div>
            {i === 4 ? (
              <div className="flex items-baseline justify-between">
                <div><div className="h-2 w-12 bg-gray-100 rounded mb-1" /><div className="h-4 w-16 bg-gray-200 rounded" /></div>
                <div><div className="h-2 w-12 bg-gray-100 rounded mb-1" /><div className="h-4 w-16 bg-gray-200 rounded" /></div>
              </div>
            ) : (
              <div className="h-6 w-12 bg-gray-200 rounded" />
            )}
          </div>
        ))}
      </div>

      {/* Leads Table Skeleton */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="h-9 flex-1 max-w-[250px] bg-gray-100 rounded-lg" />
            <div className="flex-1" />
            <div className="h-9 w-[130px] bg-gray-100 rounded-lg" />
            <div className="h-9 w-[120px] bg-gray-100 rounded-lg" />
            <div className="h-9 w-9 bg-gray-100 rounded-lg" />
            <div className="h-9 w-[120px] bg-gray-200 rounded-lg" />
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="px-3 py-3 border-b border-gray-100 flex gap-4">
            {[20,24,20,20,24,20,24,24].map((w, i) => <div key={`skel-col-${w}-${i}`} className={`h-3 w-${w} bg-gray-200 rounded`} />)}
          </div>
        </div>
        {[1,2,3,4,5,6].map(i => (
          <div key={`row-skel-${i}`} className="px-3 py-3 border-b border-gray-50 flex items-center gap-4">
            <div className="h-3.5 w-20 bg-gray-200 rounded" />
            <div className="h-3.5 w-20 bg-gray-100 rounded" />
            <div className="h-3.5 w-28 bg-gray-100 rounded" />
            <div className="h-3.5 w-20 bg-gray-100 rounded" />
            <div className="h-3.5 w-20 bg-gray-100 rounded" />
            <div className="h-5 w-14 bg-gray-100 rounded-full" />
            <div className="h-3.5 w-16 bg-gray-100 rounded" />
            <div className="h-3.5 w-12 bg-gray-100 rounded" />
          </div>
        ))}
        <div className="px-4 lg:px-6 py-3 flex items-center justify-between border-t border-gray-100">
          <div className="h-3 w-36 bg-gray-100 rounded" />
          <div className="flex items-center gap-1">
            {[1,2,3].map(i => <div key={`pag-skel-${i}`} className="w-8 h-8 bg-gray-100 rounded" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

// No partner registration state component
function NoPartnerRegistration({ onNavigate }) {
  return (
    <div className="max-w-md mx-auto text-center py-16">
      <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4"><FileText className="w-7 h-7 text-blue-600" /></div>
      <h1 className="text-lg font-semibold text-gray-900 mb-2">Complete Your Registration</h1>
      <p className="text-sm text-gray-500 mb-5">Complete the registration wizard to start earning commissions.</p>
      <button onClick={onNavigate} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="start-registration-btn">Start Registration <ArrowRight className="w-4 h-4" /></button>
    </div>
  );
}

NoPartnerRegistration.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

// Helper to get size-based classes for commission preview
function getCommissionPreviewClasses(isLarge) {
  return {
    spinnerSize: isLarge ? 'w-4 h-4' : 'w-3.5 h-3.5',
    calcText: isLarge ? 'text-[12px]' : 'text-[11px]',
    gapClass: isLarge ? 'gap-2' : 'gap-1.5',
    amountText: isLarge ? 'text-[22px]' : 'text-[18px]',
    toText: isLarge ? 'text-[14px]' : 'text-[12px]',
    rateText: isLarge ? 'text-[11px]' : 'text-[10px]',
    noConfigText: isLarge ? 'text-[12px]' : 'text-[11px]',
  };
}

// Commission preview component extracted to reduce complexity
function CommissionPreviewContent({ size, loading, commRange }) {
  const isLarge = size === 'lg';
  const cls = getCommissionPreviewClasses(isLarge);

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className={`${cls.spinnerSize} border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin`} />
        <span className={`${cls.calcText} text-gray-500`}>Calculating...</span>
      </div>
    );
  }

  if (!commRange) {
    return <p className={`${cls.noConfigText} text-gray-500`}>No commission for this Loan Type, contact RM for more Information</p>;
  }

  const { min, max, min_pct, max_pct } = commRange.commission_range;
  const isRange = min !== max;
  const rateDisplay = min_pct !== max_pct 
    ? `${min_pct?.toFixed(2)}% – ${max_pct?.toFixed(2)}%`
    : `${min_pct?.toFixed(2)}%`;

  return (
    <>
      <div className={`flex items-baseline ${cls.gapClass} flex-wrap`}>
        <span className={`${cls.amountText} font-bold text-[#1e40af] tabular-nums`}>{fmtCurrency(min)}</span>
        {isRange && (
          <>
            <span className={`${cls.toText} text-gray-400`}>to</span>
            <span className={`${cls.amountText} font-bold text-[#1e40af] tabular-nums`}>{fmtCurrency(max)}</span>
          </>
        )}
      </div>
      <p className={`${cls.rateText} text-[#10b981] mt-0.5`}>{rateDisplay} effective rate</p>
    </>
  );
}

CommissionPreviewContent.propTypes = {
  size: PropTypes.oneOf(['lg', 'sm']).isRequired,
  loading: PropTypes.bool,
  commRange: PropTypes.shape({
    commission_range: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
      min_pct: PropTypes.number,
      max_pct: PropTypes.number,
    }),
  }),
};

// Leads table loading skeleton
function LeadsLoadingSkeleton() {
  return (
    <div className="animate-pulse">
      {[1,2,3,4,5,6,7,8,9,10].map(i => (
        <div key={i} className="px-3 py-3 border-b border-gray-50 flex items-center gap-4">
          <div className="h-3.5 w-20 bg-gray-200 rounded" />
          <div className="h-3.5 w-20 bg-gray-100 rounded" />
          <div className="h-3.5 w-28 bg-gray-100 rounded" />
          <div className="h-3.5 w-20 bg-gray-100 rounded" />
          <div className="h-3.5 w-20 bg-gray-100 rounded" />
          <div className="h-5 w-16 bg-gray-100 rounded-full" />
          <div className="h-3.5 w-16 bg-gray-100 rounded" />
          <div className="h-3.5 w-14 bg-gray-100 rounded" />
        </div>
      ))}
    </div>
  );
}

// Empty state for leads
function LeadsEmptyState() {
  return (
    <div className="py-16 text-center">
      <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-500">No referrals found</p>
    </div>
  );
}

// Helper to format commission range display
function formatCommissionRange(range) {
  if (!range) return '—';
  const { min, max } = range;
  const minFormatted = fmtShortCurrency(min);
  const maxFormatted = fmtShortCurrency(max);
  return min !== max ? `${minFormatted}—${maxFormatted}` : minFormatted;
}

// Desktop table row component
function LeadTableRow({ lead, leadCommRange, onSelect }) {
  const cfg = statusConfig[lead.current_status] || { label: lead.current_status, cls: 'bg-gray-50 text-gray-600 border border-gray-200' };
  
  return (
    <tr className="border-b border-gray-50 hover:bg-blue-50/60 transition-colors cursor-pointer" onClick={() => onSelect(lead)} data-testid={`lead-row-${lead.id}`}>
      <td className="px-3 py-2">
        <p className="text-[13px] font-medium text-gray-900">{lead.customer_name}</p>
        <p className="text-[11px] text-blue-600">{lead.crm_lead_id || lead.lead_reference || '-'}</p>
      </td>
      <td className="px-3 py-2 text-gray-600">{lead.product_name || '-'}</td>
      <td className="px-3 py-2 text-gray-900 tabular whitespace-nowrap">{lead.loan_amount ? fmtCurrency(lead.loan_amount) : '-'}</td>
      <td className="px-3 py-2 tabular whitespace-nowrap">
        {lead.disbursement_amount 
          ? <span className="text-emerald-700 font-medium">{fmtCurrency(lead.disbursement_amount)}</span> 
          : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2 whitespace-nowrap"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${cfg.cls}`}>{cfg.label}</span></td>
      <td className="px-3 py-2 tabular">
        {leadCommRange 
          ? <span className="text-xs font-medium text-blue-600 whitespace-nowrap">{formatCommissionRange(leadCommRange)}</span>
          : '—'}
      </td>
      <td className="px-3 py-2 tabular">
        {lead.bank_name && lead.commission_amount
          ? <span className="text-amber-600 font-medium whitespace-nowrap">{fmtCurrency(lead.commission_amount)}</span>
          : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-3 py-2 tabular">
        {lead.commission_paid
          ? <span className="text-green-600 font-medium">{fmtCurrency(lead.commission_paid)}</span>
          : <span className="text-gray-400">—</span>}
      </td>
    </tr>
  );
}

LeadTableRow.propTypes = {
  lead: PropTypes.object.isRequired,
  leadCommRange: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

// Mobile card component for a single lead
function LeadMobileCard({ lead, leadCommRange, onSelect }) {
  const cfg = statusConfig[lead.current_status] || { label: lead.current_status, cls: 'bg-gray-50 text-gray-600 border border-gray-200' };
  
  return (
    <button className="w-full text-left px-4 py-4 active:bg-blue-50/60 transition-colors cursor-pointer" onClick={() => onSelect(lead)} data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-blue-600 font-semibold text-[13px]">{lead.crm_lead_id || lead.lead_reference || '-'}</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${cfg.cls}`}>{cfg.label}</span>
      </div>
      <p className="text-[14px] font-semibold text-gray-900 mb-1">{lead.customer_name}</p>
      <p className="text-[12px] text-gray-500 mb-3">{lead.product_name || '-'} • {lead.loan_amount ? fmtCurrency(lead.loan_amount) : '-'}</p>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-gray-400 mb-0.5">Exp. Commission</p>
          <p className="text-[13px] font-semibold text-gray-900">
            {leadCommRange 
              ? <span className="text-blue-600 whitespace-nowrap">{formatCommissionRange(leadCommRange)}</span>
              : '—'}
          </p>
        </div>
        <div className="text-center">
          <p className="text-[11px] text-gray-400 mb-0.5">Final Commission</p>
          <p className="text-[13px] font-semibold">
            {lead.bank_name && lead.commission_amount
              ? <span className="text-emerald-600 whitespace-nowrap">{fmtShortCurrency(lead.commission_amount)}</span>
              : <span className="text-gray-400">—</span>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-gray-400 mb-0.5">Comm. Paid</p>
          <p className="text-[13px] font-semibold">
            {lead.commission_paid
              ? <span className="text-green-600">{fmtShortCurrency(lead.commission_paid)}</span>
              : <span className="text-gray-400">—</span>}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 ml-2" />
      </div>
    </button>
  );
}

LeadMobileCard.propTypes = {
  lead: PropTypes.object.isRequired,
  leadCommRange: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

// Leads table content - extracted to reduce cognitive complexity
function LeadsTableContent({ loading, leads, leadCommRanges, onSelectLead }) {
  if (loading) return <LeadsLoadingSkeleton />;
  if (leads.length === 0) return <LeadsEmptyState />;

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3">Applicant</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3">Loan Type</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3">Loan Amount</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3">Disbursed Amt</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 w-[140px]">Current Stage</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 w-[140px]">Exp. Commission</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 w-[140px]">Final Commission</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 w-[140px]">Commission Paid</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(lead => (
              <LeadTableRow key={lead.id} lead={lead} leadCommRange={leadCommRanges[lead.id]} onSelect={onSelectLead} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-gray-100">
        {leads.map(lead => (
          <LeadMobileCard key={lead.id} lead={lead} leadCommRange={leadCommRanges[lead.id]} onSelect={onSelectLead} />
        ))}
      </div>
    </>
  );
}

LeadsTableContent.propTypes = {
  loading: PropTypes.bool.isRequired,
  leads: PropTypes.array.isRequired,
  leadCommRanges: PropTypes.object.isRequired,
  onSelectLead: PropTypes.func.isRequired,
};

// KPI Commission Range Card - extracted to reduce cognitive complexity
function KpiCommissionRangeCard({ loading, expectedMin, expectedMax }) {
  return (
    <div className="bg-[#ece8f7] rounded-xl p-2.5 sm:p-4 border border-[#dad2f0] shadow-sm col-span-2 lg:col-span-1 order-5 lg:order-4" data-testid="kpi-commission-earned">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <p className="text-[11px] sm:text-[12px] text-[#5b6b82] font-medium">Expected Commission Range</p>
        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#d4c9ed] flex items-center justify-center shrink-0">
          <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#6340a8]" viewBox="0 0 24 24" fill="currentColor"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>
        </div>
      </div>
      {loading ? (
        <div className="flex items-baseline justify-between animate-pulse">
          <div>
            <div className="h-2.5 w-12 bg-[#d4c9ed] rounded mb-1.5" />
            <div className="h-4 w-16 bg-[#d4c9ed] rounded" />
          </div>
          <div className="text-right">
            <div className="h-2.5 w-12 bg-[#d4c9ed] rounded mb-1.5 ml-auto" />
            <div className="h-4 w-20 bg-[#d4c9ed] rounded" />
          </div>
        </div>
      ) : (
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Minimum</p>
            <p className="text-[13px] sm:text-[14px] font-bold text-[#1a2b4a] leading-none">{fmtShortCurrency(Math.round(expectedMin))}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Maximum</p>
            <p className="text-[13px] sm:text-[14px] font-bold text-[#1a2b4a] leading-none">{fmtShortCurrency(Math.round(expectedMax))}</p>
          </div>
        </div>
      )}
    </div>
  );
}

KpiCommissionRangeCard.propTypes = {
  loading: PropTypes.bool.isRequired,
  expectedMin: PropTypes.number.isRequired,
  expectedMax: PropTypes.number.isRequired,
};

// Size configuration maps for AddLeadFormBody - split to reduce cognitive complexity
const FORM_SIZE_LG = {
  isLarge: true,
  inputHeight: 'h-[40px]',
  inputRadius: 'rounded-[10px]',
  labelClass: 'text-[12px] mb-1',
  inputText: 'text-[13px]',
  sectionText: 'text-[10px]',
  sectionPadding: 'p-3.5 lg:p-4',
  sectionRadius: 'rounded-[10px] lg:rounded-[12px]',
  gridLayout: 'grid sm:grid-cols-2 gap-2.5',
  loanGridLayout: 'grid sm:grid-cols-2 gap-2.5',
  colSpanClass: 'sm:col-span-2',
  sectionMb: 'mb-2.5',
  prefixPx: 'px-2.5',
  prefixText: 'text-[12px]',
  borderRadius: '10',
  notesPlaceholder: 'Any additional information about the customer or loan requirement...',
  notesPy: 'py-2',
  notesHeight: 'h-[72px]',
  commGapMb: 'mb-2',
  commIconSize: 'w-3.5 h-3.5',
  commLabelText: 'text-[11px]',
};

const FORM_SIZE_SM = {
  isLarge: false,
  inputHeight: 'h-[34px]',
  inputRadius: 'rounded-[8px]',
  labelClass: 'text-[11px] mb-0.5',
  inputText: 'text-[12px]',
  sectionText: 'text-[9px]',
  sectionPadding: 'p-3',
  sectionRadius: 'rounded-[10px]',
  gridLayout: 'space-y-1.5',
  loanGridLayout: 'space-y-2',
  colSpanClass: '',
  sectionMb: 'mb-2',
  prefixPx: 'px-2',
  prefixText: 'text-[11px]',
  borderRadius: '8',
  notesPlaceholder: 'Any additional information...',
  notesPy: 'py-1.5',
  notesHeight: 'h-[56px]',
  commGapMb: 'mb-1.5',
  commIconSize: 'w-3 h-3',
  commLabelText: 'text-[10px]',
};

function getFormSizeConfig(size) {
  return size === 'lg' ? FORM_SIZE_LG : FORM_SIZE_SM;
}

// Add Lead Form Body - shared between Drawer and Sheet
function AddLeadFormBody({ form, setForm, products, formCommLoading, formCommRange, size }) {
  const cfg = getFormSizeConfig(size);

  const rootLoanTypes = useMemo(() => getRootLoanTypes(products), [products]);
  const childLoanTypes = useMemo(
    () => getChildLoanTypes(products, form.parent_loan_type_id),
    [products, form.parent_loan_type_id]
  );
  const requiresSub = childLoanTypes.length > 0;
  const leafResolved = resolveLeafLoanType(products, form.parent_loan_type_id, form.sub_loan_type_id);
  const showCommPreview = leafResolved.ok && form.loan_amount;

  const handleNameChange = (e) => {
    const v = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    setForm({...form, customer_name: v});
  };

  const handleMobileChange = (e) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length <= 10) setForm({...form, customer_mobile: v});
  };

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    const num = raw ? parseInt(raw, 10) : '';
    if (num === '' || num <= 500000000) setForm({...form, loan_amount: num});
  };

  const handleParentChange = (v) => {
    setForm({
      ...form,
      parent_loan_type_id: v === 'none' ? '' : v,
      sub_loan_type_id: '', // always reset child when parent changes
    });
  };

  return (
    <>
      {/* Customer Information Section */}
      <div className={`bg-[#f8fafc] ${cfg.sectionRadius} ${cfg.sectionPadding} border border-[#edf2f7]`}>
        <p className={`${cfg.sectionText} uppercase tracking-[1px] font-bold text-[#64748b] ${cfg.sectionMb}`}>Customer Information</p>
        <div className={cfg.gridLayout}>
          <div>
            <label htmlFor="lead-customer-name" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Customer Name *</label>
            <Input id="lead-customer-name" value={form.customer_name} onChange={handleNameChange} placeholder="Enter full name" className={`${cfg.inputHeight} ${cfg.inputRadius} border-[#dbe3ec] ${cfg.inputText} pl-3`} data-testid="lead-customer-name" />
          </div>
          <div>
            <label htmlFor="lead-customer-mobile" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Mobile Number *</label>
            <div className="flex">
              <span className={`inline-flex items-center ${cfg.prefixPx} bg-white border border-r-0 border-[#dbe3ec] rounded-l-[${cfg.borderRadius}px] ${cfg.prefixText} text-[#64748b] font-medium`}>+91</span>
              <Input id="lead-customer-mobile" value={form.customer_mobile} onChange={handleMobileChange} placeholder="9876543210" maxLength={10} inputMode="numeric" className={`${cfg.inputHeight} rounded-l-none rounded-r-[${cfg.borderRadius}px] border-[#dbe3ec] ${cfg.inputText}`} data-testid="lead-customer-mobile" />
            </div>
          </div>
        </div>
      </div>

      {/* Loan Information Section */}
      <div className={`bg-[#f8fafc] ${cfg.sectionRadius} ${cfg.sectionPadding} border border-[#edf2f7]`}>
        <p className={`${cfg.sectionText} uppercase tracking-[1px] font-bold text-[#64748b] mb-2`}>Loan Information</p>
        <div className={cfg.loanGridLayout}>
          <div>
            <label htmlFor="lead-loan-type" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Loan Type *</label>
            <Select value={form.parent_loan_type_id || 'none'} onValueChange={handleParentChange}>
              <SelectTrigger id="lead-loan-type" className={`${cfg.inputHeight} ${cfg.inputRadius} border-[#dbe3ec] ${cfg.inputText} font-medium text-[#334155] hover:border-[#93c5fd]`} data-testid="lead-product-select"><SelectValue placeholder="Select Loan Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="focus:bg-[#1B6EF3] focus:text-white">Select Loan Type</SelectItem>
                {rootLoanTypes.map(p => <SelectItem key={p.id} value={p.id} className="focus:bg-[#1B6EF3] focus:text-white">{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.parent_loan_type_id && requiresSub && (
            <div>
              <label htmlFor="lead-sub-loan-type" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Sub Loan Type *</label>
              <Select
                value={form.sub_loan_type_id || 'none'}
                onValueChange={v => setForm({ ...form, sub_loan_type_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger id="lead-sub-loan-type" className={`${cfg.inputHeight} ${cfg.inputRadius} border-[#dbe3ec] ${cfg.inputText} font-medium text-[#334155] hover:border-[#93c5fd]`} data-testid="lead-sub-product-select">
                  <SelectValue placeholder="Select Sub Loan Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="focus:bg-[#1B6EF3] focus:text-white">Select Sub Loan Type</SelectItem>
                  {childLoanTypes.map(p => <SelectItem key={p.id} value={p.id} className="focus:bg-[#1B6EF3] focus:text-white">{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label htmlFor="lead-loan-amount" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Loan Amount *</label>
            <div className="flex">
              <span className={`inline-flex items-center ${cfg.prefixPx} bg-white border border-r-0 border-[#dbe3ec] rounded-l-[${cfg.borderRadius}px] ${cfg.inputText} text-[#64748b] font-medium`}>₹</span>
              <input id="lead-loan-amount" type="text" inputMode="numeric" value={form.loan_amount ? Number(form.loan_amount).toLocaleString('en-IN') : ''} onChange={handleAmountChange} placeholder="5,00,000" className={`w-full ${cfg.inputHeight} px-3 bg-white rounded-l-none rounded-r-[${cfg.borderRadius}px] border border-[#dbe3ec] ${cfg.inputText} text-[#1e293b] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`} data-testid="lead-loan-amount" />
            </div>
          </div>
          <div className={cfg.colSpanClass}>
            <label htmlFor="lead-location" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Location</label>
            <Input id="lead-location" value={form.location || ''} onChange={e => setForm({...form, location: e.target.value})} placeholder="City / Area" className={`${cfg.inputHeight} ${cfg.inputRadius} border-[#dbe3ec] ${cfg.inputText}`} data-testid="lead-location" />
          </div>
          <div className={cfg.colSpanClass}>
            <label htmlFor="lead-notes" className={`block ${cfg.labelClass} font-semibold text-[#334155]`}>Additional Notes</label>
            <textarea id="lead-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder={cfg.notesPlaceholder} rows={2} className={`w-full px-3 ${cfg.notesPy} bg-white ${cfg.inputRadius} border border-[#dbe3ec] ${cfg.inputText} text-[#1e293b] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none ${cfg.notesHeight}`} data-testid="lead-notes" />
          </div>
        </div>
      </div>

      {/* Commission Range Preview */}
      {showCommPreview && (
        <div className={`bg-[#f0f7ff] ${cfg.sectionRadius} ${cfg.sectionPadding} border border-[#e0edff]`}>
          <div className={`flex items-center gap-1.5 ${cfg.commGapMb}`}>
            <svg className={`${cfg.commIconSize} text-[#1e40af]`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            <span className={`${cfg.commLabelText} font-medium text-[#1e40af]`}>Estimated Commission Range</span>
          </div>
          <CommissionPreviewContent size={size} loading={formCommLoading} commRange={formCommRange} />
        </div>
      )}
    </>
  );
}

AddLeadFormBody.propTypes = {
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  formCommLoading: PropTypes.bool,
  formCommRange: PropTypes.object,
  size: PropTypes.oneOf(['lg', 'sm']).isRequired,
};

// Add Lead Dialog - extracted to reduce cognitive complexity
function AddLeadFormHeader({ isLarge }) {
  return (
    <div className={`flex items-center gap-${isLarge ? '3' : '2.5'}`}>
      <div className={`w-[${isLarge ? '40' : '36'}px] h-[${isLarge ? '40' : '36'}px] ${isLarge ? 'lg:w-[44px] lg:h-[44px]' : ''} rounded-[10px] ${isLarge ? 'lg:rounded-[12px]' : ''} bg-[#EEF4FF] text-[#2563eb] flex items-center justify-center shrink-0`}>
        <svg className={`w-${isLarge ? '5' : '4'} h-${isLarge ? '5' : '4'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
      </div>
      <div className="flex-1">
        <h3 className={`text-[${isLarge ? '16' : '14'}px] font-bold text-[#1e293b]`}>Add New Lead</h3>
        <p className={`text-[${isLarge ? '12' : '11'}px] text-[#64748b] mt-0.5`}>Create a new referral by filling the information below.</p>
      </div>
    </div>
  );
}

AddLeadFormHeader.propTypes = {
  isLarge: PropTypes.bool,
};

function AddLeadDialog({ open, onOpenChange, isMobile, form, setForm, products, formCommLoading, formCommRange, submitting, onSubmit }) {
  
  useEffect(() => {
    if (!isMobile || !open) return;
    
    const handleResize = () => {
      // Force a reflow to fix the white space issue when keyboard closes
      const drawerContent = document.querySelector('[data-drawer-content]');
      if (drawerContent) {
        drawerContent.style.transform = 'translateY(0)';
      }
    };
    
    // Listen for viewport resize (keyboard open/close)
    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, open]);

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent 
          className="max-h-[90vh] border-0 outline-none flex flex-col"
          data-drawer-content
        >
          <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <div className="px-3 pt-3 pb-1.5 mb-3">
              <AddLeadFormHeader isLarge />
            </div>
            <div className="px-3 pb-2 space-y-2.5">
              <AddLeadFormBody form={form} setForm={setForm} products={products} formCommLoading={formCommLoading} formCommRange={formCommRange} size="lg" />
            </div>
          </div>
          <div className="px-3 py-2.5 flex items-center justify-end gap-2.5 shrink-0 border-t border-gray-100">
            <button onClick={() => onOpenChange(false)} className="h-[38px] px-4 bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#475569] text-[13px] font-semibold rounded-[10px] transition-colors">Cancel</button>
            <button onClick={onSubmit} disabled={submitting} className="h-[38px] px-5 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 text-white text-[13px] font-semibold rounded-[10px] flex items-center gap-2 transition-colors" data-testid="lead-submit-form-btn">
              <Send className="w-4 h-4" /> {submitting ? 'Submitting...' : 'Submit Lead'}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[400px] p-0 bg-white border-l border-gray-200 flex flex-col h-full [&>button.absolute]:hidden">
        <div className="px-4 pt-3 pb-1.5 shrink-0">
          <AddLeadFormHeader isLarge={false} />
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <AddLeadFormBody form={form} setForm={setForm} products={products} formCommLoading={formCommLoading} formCommRange={formCommRange} size="sm" />
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2.5 shrink-0">
          <button onClick={() => onOpenChange(false)} className="h-[34px] px-3.5 bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#475569] text-[12px] font-semibold rounded-[8px] transition-colors">Cancel</button>
          <button onClick={onSubmit} disabled={submitting} className="h-[34px] px-4 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-50 text-white text-[12px] font-semibold rounded-[8px] flex items-center gap-1.5 transition-colors" data-testid="lead-submit-form-btn">
            <Send className="w-3.5 h-3.5" /> {submitting ? 'Submitting...' : 'Submit Lead'}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

AddLeadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  setForm: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  formCommLoading: PropTypes.bool,
  formCommRange: PropTypes.object,
  submitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

// Mobile Filter Drawer - extracted to reduce cognitive complexity
function MobileFilterDrawer({ open, onOpenChange, tempLoanTypeFilter, setTempLoanTypeFilter, tempStageFilter, setTempStageFilter, allLoanTypes, onApply }) {
  const hasFilters = tempLoanTypeFilter || tempStageFilter;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="border-0 outline-none">
        <div className="px-5 pt-4 pb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-[15px] font-bold text-[#1e293b]">Filters</h3>
            {hasFilters && (
              <button onClick={() => { setTempLoanTypeFilter(''); setTempStageFilter(''); }} className="text-[12px] font-medium text-[#1B6EF3]">Clear All</button>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label htmlFor="filter-loan-type" className="block text-[12px] font-medium text-[#475569] mb-1.5">Loan Type</label>
              <Select value={tempLoanTypeFilter || 'all'} onValueChange={v => setTempLoanTypeFilter(v === 'all' ? '' : v)}>
                <SelectTrigger id="filter-loan-type" className="h-10 w-full border-[#E2E8F0] text-[13px] font-medium text-[#334155] rounded-lg">
                  <SelectValue placeholder="All Loan Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="focus:bg-[#1B6EF3] focus:text-white">All Loan Types</SelectItem>
                  {allLoanTypes.map(type => (
                    <SelectItem key={type} value={type} className="focus:bg-[#1B6EF3] focus:text-white">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="filter-stage" className="block text-[12px] font-medium text-[#475569] mb-1.5">Stage</label>
              <Select value={tempStageFilter || 'all'} onValueChange={v => setTempStageFilter(v === 'all' ? '' : v)}>
                <SelectTrigger id="filter-stage" className="h-10 w-full border-[#E2E8F0] text-[13px] font-medium text-[#334155] rounded-lg">
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="focus:bg-[#1B6EF3] focus:text-white">All Stages</SelectItem>
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="focus:bg-[#1B6EF3] focus:text-white">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <button onClick={onApply} className="w-full mt-5 h-10 bg-[#1B6EF3] hover:bg-[#1559cc] text-white text-[13px] font-semibold rounded-lg transition-colors">
            Apply Filters
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

MobileFilterDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  tempLoanTypeFilter: PropTypes.string.isRequired,
  setTempLoanTypeFilter: PropTypes.func.isRequired,
  tempStageFilter: PropTypes.string.isRequired,
  setTempStageFilter: PropTypes.func.isRequired,
  allLoanTypes: PropTypes.array.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loanTypeFilter, setLoanTypeFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [products, setProducts] = useState([]);
  const [allLoanTypes, setAllLoanTypes] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [tempLoanTypeFilter, setTempLoanTypeFilter] = useState('');
  const [tempStageFilter, setTempStageFilter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_LEAD_FORM });
  const [kpiStats, setKpiStats] = useState({ totalReferrals: 0, inProgress: 0, approved: 0, expectedCommissionMin: 0, expectedCommissionMax: 0, commissionPaid: 0 });
  const [commissionRangeLoading, setCommissionRangeLoading] = useState(true);
  const [leadCommRanges, setLeadCommRanges] = useState({});
  const [formCommRange, setFormCommRange] = useState(null);
  const [formCommLoading, setFormCommLoading] = useState(false);

  const dateParams = '';

  // Helper to process KPI leads data and commission ranges
  const processKpiData = async (kpiLeadsRes, passbookRes) => {
    const allLeads = kpiLeadsRes.value.data.leads || [];
    const total = kpiLeadsRes.value.data.total || allLeads.length;
    const inProgress = allLeads.filter(l => ['application_submitted', 'bank_assigned', 'sanctioned', 'sanctioned_pdd'].includes(l.current_status)).length;
    const approved = allLeads.filter(l => ['commission_processing', 'commission_paid'].includes(l.current_status)).length;
    const uniqueTypes = [...new Set(allLeads.map(l => l.product_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    setAllLoanTypes(uniqueTypes);
    
    const rangeableLeads = allLeads.filter(l => !['commission_paid'].includes(l.current_status));
    let rangeMin = 0;
    let rangeMax = 0;
    const perLeadRanges = {};
    const previews = await processCommissionPreviews(rangeableLeads);
    for (const p of previews) {
      if (!p?.data?.configs?.length) continue;
      const range = p.data.commission_range;
      if (range && (range.min > 0 || range.max > 0)) {
        perLeadRanges[p.id] = { min: range.min, max: range.max };
        rangeMin += range.min;
        rangeMax += range.max;
      }
    }
    setLeadCommRanges(perLeadRanges);
    
    // Get passbook totals
    let commissionPaid = 0;
    if (passbookRes.status === 'fulfilled') {
      const totals = passbookRes.value.data.totals || {};
      commissionPaid = totals.paid || 0;
    }
    
    setKpiStats({ totalReferrals: total, inProgress, approved, expectedCommissionMin: rangeMin, expectedCommissionMax: rangeMax, commissionPaid });
  };

  // Consolidated initial data fetch to reduce multiple re-renders
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setCommissionRangeLoading(true);
      
      try {
        // Fetch all initial data in parallel
        const [dashboardRes, loanTypesRes, passbookRes, kpiLeadsRes] = await Promise.allSettled([
          api.get('/api/partners/my/dashboard'),
          api.get('/api/loan-types/active'),
          api.get(`/api/passbook?page=1&page_size=1${dateParams}`),
          api.get(`/api/leads/my?page=1&page_size=1000${dateParams}`)
        ]);

        // Process dashboard data
        if (dashboardRes.status === 'fulfilled') {
          setData(dashboardRes.value.data);
        }

        // Process loan types (keep parent_id for hierarchy dropdowns)
        if (loanTypesRes.status === 'fulfilled') {
          setProducts(mapActiveLoanTypes(loanTypesRes.value.data || {}));
        }

        // Process KPI stats and commission ranges
        if (kpiLeadsRes.status === 'fulfilled') {
          await processKpiData(kpiLeadsRes, passbookRes);
        }
      } catch (error) {
        // Error handling - components will show empty/error states
      } finally {
        setLoading(false);
        setCommissionRangeLoading(false);
      }
    };

    fetchInitialData();
  }, [dateParams]);

  const fetchKpiStats = useCallback(() => {
    setCommissionRangeLoading(true);
    api.get(`/api/leads/my?page=1&page_size=1000${dateParams}`).then(async (res) => {
      const allLeads = res.data.leads || [];
      const total = res.data.total || allLeads.length;
      const inProgress = allLeads.filter(l => ['application_submitted', 'bank_assigned', 'sanctioned', 'sanctioned_pdd'].includes(l.current_status)).length;
      const approved = allLeads.filter(l => ['commission_processing', 'commission_paid'].includes(l.current_status)).length;
      const uniqueTypes = [...new Set(allLeads.map(l => l.product_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      setAllLoanTypes(uniqueTypes);
      const rangeableLeads = allLeads.filter(l => !['commission_paid'].includes(l.current_status));
      let rangeMin = 0;
      let rangeMax = 0;
      const perLeadRanges = {};
      const previews = await processCommissionPreviews(rangeableLeads);
      for (const p of previews) {
        if (!p?.data?.configs?.length) continue;
        const range = p.data.commission_range;
        if (range && (range.min > 0 || range.max > 0)) {
          perLeadRanges[p.id] = { min: range.min, max: range.max };
          rangeMin += range.min;
          rangeMax += range.max;
        }
      }
      setLeadCommRanges(perLeadRanges);
      setKpiStats(prev => ({ ...prev, totalReferrals: total, inProgress, approved, expectedCommissionMin: rangeMin, expectedCommissionMax: rangeMax }));
      setCommissionRangeLoading(false);
    }).catch(() => { setCommissionRangeLoading(false); });

    api.get(`/api/passbook?page=1&page_size=1${dateParams}`).then(res => {
      const totals = res.data.totals || {};
      setKpiStats(prev => ({ ...prev, commissionPaid: totals.paid || 0 }));
    }).catch(() => {});
  }, [dateParams]);

  const fetchLeads = useCallback(() => {
    setLeadsLoading(true);
    let filterParams = `&page_size=10${dateParams}`;
    if (loanTypeFilter) filterParams += `&product_name=${encodeURIComponent(loanTypeFilter)}`;
    if (stageFilter) filterParams += `&status=${encodeURIComponent(stageFilter)}`;
    api.get(`/api/leads/my?page=${page}${filterParams}`).then(res => {
      setLeads(res.data.leads); setTotalPages(res.data.total_pages || 1); setTotalLeads(res.data.total || res.data.leads?.length || 0);
    }).catch(() => {}).finally(() => setLeadsLoading(false));
  }, [page, dateParams, loanTypeFilter, stageFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { setPage(1); }, [loanTypeFilter, stageFilter]);

  useEffect(() => {
    const resolved = resolveLeafLoanType(products, form.parent_loan_type_id, form.sub_loan_type_id);
    if (!resolved.ok || !resolved.leaf) { setFormCommRange(null); return; }
    const selectedProduct = resolved.leaf;
    const loanAmt = form.loan_amount ? parseInt(form.loan_amount, 10) : 2500000;
    if (loanAmt <= 0) { setFormCommRange(null); return; }
    const debounceTimer = setTimeout(async () => {
      setFormCommLoading(true);
      try {
        const res = await api.post('/api/commission/range-calculate', {
          product_name: selectedProduct.name,
          product_code: selectedProduct.code,
          loan_amount: loanAmt,
          loan_type_id: selectedProduct.id,
        });
        if (res.data?.commission_range && (res.data.commission_range.min > 0 || res.data.commission_range.max > 0)) {
          setFormCommRange(res.data);
        } else {
          setFormCommRange(null);
        }
      } catch { setFormCommRange(null); }
      setFormCommLoading(false);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [form.parent_loan_type_id, form.sub_loan_type_id, form.loan_amount, products]);

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_mobile || !form.loan_amount) { toast.error('Please fill all required fields'); return; }
    if (!/^[a-zA-Z\s]+$/.test(form.customer_name)) { toast.error('Customer name must contain only alphabetic characters'); return; }
    if (!/^\d{10}$/.test(form.customer_mobile)) { toast.error('Customer mobile must be exactly 10 digits'); return; }
    const resolved = resolveLeafLoanType(products, form.parent_loan_type_id, form.sub_loan_type_id);
    if (!resolved.ok) { toast.error(resolved.error); return; }
    const leaf = resolved.leaf;
    setSubmitting(true);
    try {
      const res = await api.post('/api/leads/submit', {
        customer_name: form.customer_name,
        customer_mobile: form.customer_mobile,
        product_id: leaf.id,
        product_code: leaf.code || '',
        product_name: leaf.name || '',
        parent_loan_type_id: form.parent_loan_type_id || null,
        loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null,
        location: form.location || null,
        notes: form.notes || null,
      });
      toast.success(`Lead submitted! Ref: ${res.data.lead_reference}`);
      setShowDialog(false); setForm({ ...EMPTY_LEAD_FORM }); setFormCommRange(null);
      fetchLeads(); fetchKpiStats(); api.get('/api/partners/my/dashboard').then(r => setData(r.data)).catch(() => {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit lead'); }
    setSubmitting(false);
  };

  const handleExport = async () => {
    try {
      const allLeads = await fetchAndExportLeads({ dateParams, loanTypeFilter, stageFilter, searchQuery });
      if (allLeads.length === 0) { toast.error('No entries to download'); return; }
      downloadCsv(buildLeadsCsv(allLeads), 'lead_referrals.csv');
      toast.success(`Downloaded ${allLeads.length} entries`);
    } catch { toast.error('Failed to download'); }
  };

  const handleApplyFilters = () => {
    setLoanTypeFilter(tempLoanTypeFilter);
    setStageFilter(tempStageFilter);
    setShowFilterDrawer(false);
  };

  const handleOpenFilterDrawer = () => {
    setTempLoanTypeFilter(loanTypeFilter);
    setTempStageFilter(stageFilter);
    setShowFilterDrawer(true);
  };

  const filteredLeads = searchQuery
    ? leads.filter(l => (l.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (l.crm_lead_id || l.lead_reference || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : leads;

  if (loading) return <PartnerDashboardSkeleton />;
  if (!data?.has_partner) return <NoPartnerRegistration onNavigate={() => navigate('/partner/register')} />;

  return (
    <div data-testid="partner-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
        <div className="bg-[#e8eff8] rounded-xl p-2.5 sm:p-4 border border-[#d0dff0] shadow-sm" data-testid="kpi-total-referrals">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <p className="text-[11px] sm:text-[12px] text-[#5b6b82] font-medium">Total Referrals</p>
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#c8daf0] flex items-center justify-center shrink-0">
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#3a6bb5]" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
            </div>
          </div>
          <p className="text-[20px] sm:text-[22px] font-bold text-[#1a2b4a] leading-none">{kpiStats.totalReferrals}</p>
        </div>

        <div className="bg-[#e4f5ec] rounded-xl p-2.5 sm:p-4 border border-[#c8eadb] shadow-sm" data-testid="kpi-in-progress">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <p className="text-[11px] sm:text-[12px] text-[#5b6b82] font-medium leading-tight">Applications In Progress</p>
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#b8e4ce] flex items-center justify-center shrink-0 ml-1">
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#1f8a50]" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
            </div>
          </div>
          <p className="text-[20px] sm:text-[22px] font-bold text-[#1a2b4a] leading-none">{kpiStats.inProgress}</p>
        </div>

        <div className="bg-[#e2f4ef] rounded-xl p-2.5 sm:p-4 border border-[#c5ead9] shadow-sm" data-testid="kpi-approved">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <p className="text-[11px] sm:text-[12px] text-[#5b6b82] font-medium">Approved Loans</p>
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#b5e3d2] flex items-center justify-center shrink-0">
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#1a8a4f]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            </div>
          </div>
          <p className="text-[20px] sm:text-[22px] font-bold text-[#1a2b4a] leading-none">{kpiStats.approved}</p>
        </div>

        <KpiCommissionRangeCard loading={commissionRangeLoading} expectedMin={kpiStats.expectedCommissionMin} expectedMax={kpiStats.expectedCommissionMax} />

        <div className="bg-[#faf0e2] rounded-xl p-2.5 sm:p-4 border border-[#f2dfc4] shadow-sm order-4 lg:order-5" data-testid="kpi-commission-paid">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <p className="text-[11px] sm:text-[12px] text-[#5b6b82] font-medium">Commission Paid</p>
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-[#f5d9a8] flex items-center justify-center shrink-0">
              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#b06a12]" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/></svg>
            </div>
          </div>
          <p className="text-[20px] sm:text-[22px] font-bold text-[#1a2b4a] leading-none whitespace-nowrap"><span className="sm:hidden">{fmtShortCurrency(kpiStats.commissionPaid)}</span><span className="hidden sm:inline">{fmtCurrency(kpiStats.commissionPaid)}</span></p>
        </div>
      </div>

      {/* Recent Loan Referrals */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="dashboard-leads-table">
        {/* Header */}
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-100">
          {/* Desktop: single row */}
          <div className="hidden lg:flex items-center gap-2">
            <div className="relative w-[25%] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by Application ID or Name" className="w-full h-9 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-gray-400" data-testid="leads-search-input" />
            </div>
            <div className="flex-1" />
            <Select value={loanTypeFilter} onValueChange={v => setLoanTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] border-[#E2E8F0] text-sm font-medium text-[#334155] rounded-lg hover:border-[#93c5fd]" data-testid="loan-type-filter">
                <SelectValue placeholder="All Loan Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="focus:bg-[#1B6EF3] focus:text-white">All Loan Types</SelectItem>
                {allLoanTypes.map(type => (
                  <SelectItem key={type} value={type} className="focus:bg-[#1B6EF3] focus:text-white">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stageFilter} onValueChange={v => setStageFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[120px] border-[#E2E8F0] text-sm font-medium text-[#334155] rounded-lg hover:border-[#93c5fd]" data-testid="stage-filter">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="focus:bg-[#1B6EF3] focus:text-white">All Stages</SelectItem>
                {Object.entries(statusConfig).map(([key, { label }]) => (
                  <SelectItem key={key} value={key} className="focus:bg-[#1B6EF3] focus:text-white">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={handleExport} className="inline-flex items-center gap-1.5 h-9 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 text-sm font-medium shrink-0" title="Export leads to CSV">
              <Download className="w-4 h-4" />
              Export
            </button>
            <button onClick={() => setShowDialog(true)} className="inline-flex items-center gap-2 h-9 px-4 bg-[#1B6EF3] hover:bg-[#1559cc] text-white text-sm font-semibold rounded-lg transition-colors" data-testid="dashboard-new-lead-btn">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
              Add New Lead
            </button>
          </div>

          {/* Mobile: single row with search, filter, download, add lead */}
          <div className="lg:hidden flex items-center gap-1.5">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID or Name" className="w-full h-8 pl-8 pr-2 text-[12px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 placeholder:text-gray-400" />
            </div>
            <button onClick={handleOpenFilterDrawer} className={`h-8 w-8 flex items-center justify-center border rounded-lg shrink-0 transition-colors ${(loanTypeFilter || stageFilter) ? 'border-[#1B6EF3] bg-blue-50 text-[#1B6EF3]' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`} aria-label="Filters">
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
            <button onClick={handleExport} className="h-8 w-8 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 shrink-0" title="Export leads to CSV" aria-label="Export CSV">
              <Download className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setShowDialog(true)} className="h-8 w-8 flex items-center justify-center bg-[#1B6EF3] hover:bg-[#1559cc] text-white rounded-lg shrink-0" data-testid="dashboard-new-lead-btn" aria-label="Add Lead">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
            </button>
          </div>

          <AddLeadDialog open={showDialog} onOpenChange={setShowDialog} isMobile={isMobile} form={form} setForm={setForm} products={products} formCommLoading={formCommLoading} formCommRange={formCommRange} submitting={submitting} onSubmit={handleSubmit} />
        </div>

        {/* Table (Desktop) / Cards (Mobile) */}
        <div>
          <LeadsTableContent loading={leadsLoading} leads={filteredLeads} leadCommRanges={leadCommRanges} onSelectLead={setSelectedLead} />
        </div>

        {/* Pagination */}
        <TablePagination page={page} totalPages={totalPages} total={totalLeads} pageSize={10} onPageChange={setPage} />
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} />}

      <MobileFilterDrawer open={showFilterDrawer} onOpenChange={setShowFilterDrawer} tempLoanTypeFilter={tempLoanTypeFilter} setTempLoanTypeFilter={setTempLoanTypeFilter} tempStageFilter={tempStageFilter} setTempStageFilter={setTempStageFilter} allLoanTypes={allLoanTypes} onApply={handleApplyFilters} />
    </div>
  );
}
